// The finalize screen: everything is signed, and this is the one place
// money moves. It recaps exactly what will be charged (from the frozen
// agreement the server re-verifies before charging), names the card, and
// submits. A decline surfaces the server's family-facing copy (keyed off
// the decline code — never the processor's words) beside the
// change-payment affordance; a slow installment first-cycle hands off to
// a processing face that polls the view until the server settles it.

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRightIcon } from "@heroicons/react/20/solid";

import Button from "components/Button";
import { NetworkError } from "hooks/usePublicAPI";

import { useQuoteFlowAnalytics } from "./analytics";
import {
  fetchFinalizeView,
  submitFinalize,
  type ChargeDeclinedResponse,
  type FinalizeView,
} from "./api";
import DeclineNotice from "./DeclineNotice";
import ScreenChrome from "components/ScreenChrome";
import LoadingScreen from "./LoadingScreen";
import { dollars, finalizeDueNote } from "./payment-copy";

const PROCESSING_POLL_MS = 2_500;

const LOADING_STATUSES = ["Preparing your summary…"] as const;
const PROCESSING_STATUSES = [
  "Processing your payment…",
  "Confirming with your card issuer…",
  "Recording your policy…",
] as const;

/** The card brand, dressed for reading. */
const BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  "american-express": "American Express",
  discover: "Discover",
  "diners-club": "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
};

function brandLabel(brand: string | null): string {
  if (!brand) return "Card";
  return BRAND_LABELS[brand] ?? brand;
}

function fallbackPlanLabel(planSlug: string | null): string {
  if (!planSlug) return "";
  return planSlug.charAt(0).toUpperCase() + planSlug.slice(1);
}

interface StepRow {
  n: string;
  label: string;
  value: string;
  note: string;
}

/** What the charged-today card says under the number, and the two
 * numbered rows beneath — per payment shape. */
function chargedTodayContent(
  view: FinalizeView,
  plan: string,
  paymentPeriodOap: string | null,
): {
  note: string;
  steps: StepRow[];
} {
  const paidInFull = view.amount.payment_period === "single_pay";
  const due = view.amount.due_today_cents ?? 0;
  const recurring =
    view.amount.recurring_cents !== null
      ? dollars(view.amount.recurring_cents)
      : null;
  const carrier = view.carrier?.name ?? "your insurance carrier";

  if (paidInFull) {
    return {
      note: `Covers your ${plan} policy in one payment — today's prices locked in, with coverage anywhere in the U.S.`,
      steps: [
        {
          n: "1",
          label: "Then, each month",
          value: "Nothing",
          note: "You're paid in full. No monthly payments, ever.",
        },
        {
          n: "2",
          label: "From today on",
          value: "You're covered",
          note: "Your arrangements are funded at today's price, wherever you are.",
        },
      ],
    };
  }

  const monthlyStep: StepRow = {
    n: "1",
    label: "Then, each month",
    value: recurring ?? "Your monthly payment",
    note: `Billed monthly by ${carrier}.`,
  };
  const paidOffStep: StepRow = {
    n: "2",
    label: "When it's paid off",
    value: "Nothing more",
    note: "Your plan is fully funded at today's price. If you pass away before then, the balance is covered and your family owes nothing.",
  };

  return {
    note: finalizeDueNote({ payment_period_oap: paymentPeriodOap }, due, plan),
    steps: [monthlyStep, paidOffStep],
  };
}

interface Decline {
  message: string;
  /** Whether the same card is worth another try — the server's call. */
  allowRetry: boolean;
}

const FALLBACK_DECLINE: Decline = {
  message:
    "That payment didn't go through. You can try again or use a different card.",
  allowRetry: true,
};

/** The family's reading of a failed charge — the server's decline copy
 * and retry verdict when the 402 carried them, the flow's own fallback
 * otherwise. */
function declineFrom(error: unknown): Decline {
  if (error instanceof NetworkError) {
    const data = error.responseData as Partial<ChargeDeclinedResponse> | null;
    if (data?.message) {
      return { message: data.message, allowRetry: data.allow_retry !== false };
    }
  }
  return FALLBACK_DECLINE;
}

export default function FinalizeStep({
  quoteId,
  planName,
  optionSlug,
  paymentPeriodOap,
  onDone,
  onChangePayment,
}: {
  quoteId: string;
  /** The selected plan's display name (Crest, Summit, Base) — the flow
   * resolves it from the priced view; the slug fills in when it can't. */
  planName: string | null;
  /** The chosen payment option's slug — stamped on `quote_flow_completed`.
   * FinalizeView carries no option_slug of its own (only plan_slug/
   * payment_period), so this comes from the flow's settled selection. */
  optionSlug: string | null;
  /** The selection's membership scenario (`payment_period_oap`) —
   * FinalizeView carries only the payment period, so this too comes from
   * the flow's settled selection. */
  paymentPeriodOap: string | null;
  /** The charge settled (or the nothing-due submit recorded). */
  onDone: () => void;
  /** Back to the payment step to swap the instrument. */
  onChangePayment: () => void;
}) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  const [view, setView] = useState<FinalizeView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [problem, setProblem] = useState<Decline | null>(null);
  const doneRef = useRef(false);

  const finish = useCallback(
    (settled: FinalizeView) => {
      if (doneRef.current) return;
      doneRef.current = true;
      track("quote_flow_completed", {
        plan_slug: settled.amount.plan_slug,
        option_slug: optionSlug,
        amount_cents:
          settled.charge.amount_cents ?? settled.amount.due_today_cents ?? 0,
      });
      onDone();
    },
    [onDone, optionSlug, track],
  );

  // The read — and the resume: a reload mid-processing (or a settled
  // charge the server finished without this tab) lands where it should.
  useEffect(() => {
    let cancelled = false;
    fetchFinalizeView(quoteId)
      .then((loaded) => {
        if (cancelled) return;
        setView(loaded);
        if (loaded.charge.state === "succeeded") finish(loaded);
        else if (loaded.charge.state === "processing") setProcessing(true);
      })
      .catch((error) => {
        if (cancelled) return;
        reportFailure("fetch_finalize_view", error);
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, finish, reportFailure]);

  // The processing face polls the view; the server settles the attempt
  // from its own observation of the billing objects.
  useEffect(() => {
    if (!processing) return undefined;
    // One report per poll session: the interval has no retry budget, and a
    // dead gateway would otherwise file an event every tick.
    let failureReported = false;
    const interval = setInterval(() => {
      fetchFinalizeView(quoteId)
        .then((fresh) => {
          setView(fresh);
          if (fresh.charge.state === "succeeded") {
            setProcessing(false);
            finish(fresh);
          } else if (fresh.charge.state !== "processing") {
            // The attempt settled as failed server-side — no decline body
            // to read a code from, only the poll's own observation.
            setProcessing(false);
            track("quote_flow_finalize_payment_declined", {
              decline_code: null,
              allow_retry: FALLBACK_DECLINE.allowRetry,
            });
            setProblem(FALLBACK_DECLINE);
          }
        })
        .catch((error) => {
          if (failureReported) return;
          failureReported = true;
          reportFailure("fetch_finalize_view", error);
        });
    }, PROCESSING_POLL_MS);
    return () => clearInterval(interval);
  }, [processing, quoteId, finish, track, reportFailure]);

  const submit = () => {
    if (submitting || !view) return;
    setSubmitting(true);
    setProblem(null);
    track("quote_flow_finalize_submitted", {
      due_today_cents: view.amount.due_today_cents,
    });
    submitFinalize(quoteId)
      .then((fresh) => {
        setView(fresh);
        setSubmitting(false);
        if (fresh.charge.state === "succeeded") finish(fresh);
        else if (fresh.charge.state === "processing") setProcessing(true);
        else {
          track("quote_flow_finalize_payment_declined", {
            decline_code: null,
            allow_retry: FALLBACK_DECLINE.allowRetry,
          });
          setProblem(FALLBACK_DECLINE);
        }
      })
      .catch((error) => {
        setSubmitting(false);
        setProblem(declineFrom(error));
        // A 402 is the carrier/processor declining the charge — an
        // expected, family-facing outcome with its own copy above, not a
        // bug. Anything else is a real submit failure.
        if (error instanceof NetworkError && error.statusCode === 402) {
          const data =
            error.responseData as Partial<ChargeDeclinedResponse> | null;
          track("quote_flow_finalize_payment_declined", {
            decline_code: data?.decline_code ?? null,
            allow_retry: data?.allow_retry ?? true,
          });
        } else {
          reportFailure("submit_finalize", error);
        }
      });
  };

  if (loadFailed) {
    return (
      <ScreenChrome
        kicker="Last step"
        titleA="Something"
        titleB="hiccuped"
        blurb=""
      >
        <p className="mt-8 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
          We couldn&rsquo;t load your summary. Refresh the page to try again.
        </p>
      </ScreenChrome>
    );
  }

  if (!view) {
    return (
      <LoadingScreen
        kicker="Last step"
        titleA="Preparing your"
        titleB="summary"
        statuses={LOADING_STATUSES}
      />
    );
  }

  if (processing) {
    return (
      <LoadingScreen
        kicker="Last step"
        titleA="Finishing"
        titleB="up"
        statuses={PROCESSING_STATUSES}
        showProgressDots
      />
    );
  }

  const due = view.amount.due_today_cents ?? 0;
  const plan = planName ?? fallbackPlanLabel(view.amount.plan_slug);
  const { note, steps } = chargedTodayContent(view, plan, paymentPeriodOap);
  const buttonLabel = due > 0 ? "Pay and submit" : "Submit";
  const card = view.payment_method;

  return (
    <ScreenChrome
      kicker="Last step"
      titleA="Finalize your"
      titleB={`${plan} policy`}
      blurb="Everything's signed. Here's exactly what we'll charge, and when."
    >
      <div className="mt-8 overflow-hidden rounded-xl border border-oat-300 bg-white">
        <div className="bg-oat px-5 py-5">
          <p className="text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
            Charged today
          </p>
          <p className="mt-1 font-serif text-4xl text-soil">{dollars(due)}</p>
          <p className="mt-2 text-base leading-relaxed text-oat-800">{note}</p>
        </div>
        <div className="divide-y divide-oat-200">
          {steps.map((step) => (
            <div key={step.n} className="flex gap-4 px-5 py-4">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-oat text-sm font-bold text-soil">
                {step.n}
              </span>
              <div className="min-w-0">
                <p className="text-base text-soil">
                  <span className="text-oat-700">{step.label}</span>{" "}
                  <span className="font-bold">{step.value}</span>
                </p>
                <p className="mt-0.5 text-sm leading-relaxed text-oat-700">
                  {step.note}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {card && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-oat px-4 py-3.5">
          <p className="min-w-0 truncate text-base text-soil">
            {brandLabel(card.brand)} ending{" "}
            <span className="ph-no-capture">{card.last_4 ?? "····"}</span>
            {card.exp ? (
              <>
                {" "}
                · expires <span className="ph-no-capture">{card.exp}</span>
              </>
            ) : null}
          </p>
          <button
            type="button"
            onClick={onChangePayment}
            className="shrink-0 text-base font-bold text-evergreen underline underline-offset-2 hover:text-soil"
          >
            Change
          </button>
        </div>
      )}

      {problem !== null && (
        <DeclineNotice
          message={problem.message}
          secondary={{
            label: "Change payment method",
            onClick: onChangePayment,
          }}
        />
      )}

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pb-16">
        {due > 0 ? (
          <p className="max-w-screen-2xs text-sm leading-relaxed text-oat-700">
            By submitting, you authorize After to charge {dollars(due)} today.
          </p>
        ) : (
          <p className="max-w-screen-2xs text-sm leading-relaxed text-oat-700">
            Nothing is charged today — submitting sends your application for
            review.
          </p>
        )}
        <Button
          variant="continue"
          // A decline only a different card can fix keeps the same card's
          // submit closed; the notice's change-payment button is the way on.
          disabled={problem?.allowRetry === false}
          isLoading={submitting}
          loadingText="Processing…"
          icon={<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />}
          iconPosition="right"
          onClick={submit}
        >
          {buttonLabel}
        </Button>
      </div>
    </ScreenChrome>
  );
}
